import { type CalendarDate, type DateValue, isWeekend, type Time, today } from '@internationalized/date';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDisplayValue, jsDateToCalendarDate, TIME_ZONE, toJsDate } from '../shared/dateUtils';
import DatePickerShell from '../shared/DatePickerShell/DatePickerShell';
import { usePopoverDateValue } from '../shared/usePopoverDateValue';

/**
 * DatePicker — date (and optional time) picker whose trigger is a **read-only `InputText`** (calendar icon)
 * that opens a `Popover` with react-aria's `Calendar` and, with `showHour`, a segmented `TimeField`. The field
 * is never typed into; the value changes only from the popover.
 *
 * This is the **simple, live-commit** variant: it has no Save/Cancel — every calendar/time edit fires `onChange`
 * immediately and the trigger reflects it live. Dismissing (Escape / outside click) keeps the selection. For a
 * transactional flow with Save/Cancel/Clear and an extra-fields slot, use `DatePickerActions`.
 *
 * Public contract uses plain JS `Date`: accepts `value: Date | string | null` and emits a `Date | null` via
 * `onChange`. All `@internationalized/date` conversion lives in `../shared/dateUtils`.
 */

export type DatePickerProps = {
  disabled?: boolean;
  // Shows a native year <select> in the calendar header (month stays on prev/next).
  hasYearPicker?: boolean;
  isLabelHidden?: boolean;
  isNonModal?: boolean;
  label?: string;
  maxDate?: Date | string | null;
  minDate?: Date | string | null;
  onChange?: (date: Date | null) => void;
  // When provided, the trigger shows a clear (×) icon while a value is set; clicking it clears the field.
  onClear?: () => void;
  placeholder?: string;
  shouldDisablePast?: boolean;
  shouldExcludeWeekends?: boolean;
  showHour?: boolean;
  value?: Date | string | null;
};

function DatePicker({
  disabled = false,
  hasYearPicker = false,
  isLabelHidden = false,
  isNonModal = false,
  label,
  maxDate = null,
  minDate = null,
  onChange = () => {},
  onClear,
  placeholder,
  shouldDisablePast = true,
  shouldExcludeWeekends = false,
  showHour = false,
  value = null,
}: DatePickerProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const dateState = usePopoverDateValue(toJsDate(value));

  // Re-sync the internal value when the consumer updates `value` from outside. With no actions the picker
  // commits live, so this internal value *is* the value and the trigger follows it directly.
  const [prevValue, setPrevValue] = React.useState<Date | string | null | undefined>(value);
  if (value !== prevValue) {
    setPrevValue(value);
    dateState.setFromJsDate(toJsDate(value));
  }

  // An explicit minDate wins; otherwise fall back to "no past dates" when shouldDisablePast is on.
  const minValue = jsDateToCalendarDate(toJsDate(minDate)) ?? (shouldDisablePast ? today(TIME_ZONE) : undefined);
  const maxValue = jsDateToCalendarDate(toJsDate(maxDate)) ?? undefined;
  const isDateUnavailable = shouldExcludeWeekends ? (date: DateValue) => isWeekend(date, i18n.language) : undefined;

  function handleDateChange(nextDate: CalendarDate): void {
    onChange(dateState.changeDate(nextDate));
  }

  function handleTimeChange(nextTime: Time | null): void {
    onChange(dateState.changeTime(nextTime));
  }

  // Clears the field via the consumer's dedicated onClear (which resets its value); we also clear the internal
  // value so the trigger empties immediately. Deliberately not an onChange(null), to mirror the v1 behavior.
  function handleClear(): void {
    dateState.setFromJsDate(null);
    onClear?.();
  }

  function open(): void {
    if (!disabled) setIsOpen(true);
  }

  // react-aria's Popover reports both open and close through one boolean callback; mirror it straight into
  // state, gating on `disabled` so a disabled trigger can't be opened. (No branch: setIsOpen takes the value.)
  function handleOpenChange(nextOpen: boolean): void {
    setIsOpen(nextOpen && !disabled);
  }

  const accessibleName = label || placeholder || t('common:Select date');

  return (
    <DatePickerShell
      accessibleName={accessibleName}
      dateValue={dateState.dateValue}
      disabled={disabled}
      displayValue={formatDisplayValue(dateState.jsDate, showHour, i18n.language)}
      hasYearPicker={hasYearPicker}
      isDateUnavailable={isDateUnavailable}
      isLabelHidden={isLabelHidden}
      isNonModal={isNonModal}
      isOpen={isOpen}
      label={label}
      locale={i18n.language}
      maxValue={maxValue}
      minValue={minValue}
      onClear={onClear ? handleClear : undefined}
      onDateChange={handleDateChange}
      onOpen={open}
      onOpenChange={handleOpenChange}
      onTimeChange={handleTimeChange}
      placeholder={placeholder || t('common:Select date')}
      showHour={showHour}
      timeValue={dateState.timeValue}
    />
  );
}

export default DatePicker;
